import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  CreditCard,
  Copy,
  Upload,
  CheckCircle2,
  Clock,
  ExternalLink,
  Sparkles,
  X,
  AlertCircle,
  Building,
  User,
  Hash
} from 'lucide-react';
import { showToast } from '@/lib/custom-toast';

import BACKEND_URL from '@/config/backendUrl';


const PublicSharedExpense = () => {
  const { token } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Comprobante upload
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const [receiptUrl, setReceiptUrl] = useState('');

  // Doble Check Dialog (Iniciar sesión vs Registrarse)
  const [showClaimDialog, setShowClaimDialog] = useState(false);

  useEffect(() => {
    fetchSharedData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchSharedData = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${BACKEND_URL}/api/public/shared-expense/${token}`);
      setData(res.data);
      if (res.data.receipt_url) {
        setReceiptUrl(res.data.receipt_url);
      }
    } catch (err) {
      console.error('Error fetching public shared expense:', err);
      setError('Esta liga de cobro no existe o ha expirado.');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text, label) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    showToast.success(`${label} copiado al portapapeles`);
  };

  const handleReceiptFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      showToast.error('El archivo no debe exceder 5MB');
      return;
    }

    setUploadingReceipt(true);
    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64 = reader.result;
        await axios.post(`${BACKEND_URL}/api/public/shared-expense/${token}/upload-receipt`, {
          receipt_url: base64
        });
        setReceiptUrl(base64);
        showToast.success('¡Comprobante subido! El acreedor lo verificará.');
        fetchSharedData();
      };
      reader.readAsDataURL(file);
    } catch (err) {
      console.error('Error uploading receipt:', err);
      showToast.error('No se pudo subir el comprobante.');
    } finally {
      setUploadingReceipt(false);
    }
  };

  const handleProceedToAuth = (action) => {
    localStorage.setItem('pending_debt_claim_token', token);
    if (action === 'login') {
      navigate(`/login?claim_token=${token}`);
    } else {
      navigate(`/register?claim_token=${token}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-500" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-slate-900 border border-slate-800 rounded-3xl p-8 text-center space-y-4">
          <AlertCircle className="w-12 h-12 text-rose-500 mx-auto" />
          <h2 className="text-xl font-black">Liga no encontrada</h2>
          <p className="text-sm text-slate-400">{error || 'El cobro solicitado no está disponible.'}</p>
        </div>
      </div>
    );
  }

  const isPaid = data.status === 'paid';
  const hasReceipt = Boolean(receiptUrl || data.receipt_url);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col justify-between py-8 px-4 sm:px-6 relative overflow-hidden">
      {/* Background glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-96 bg-indigo-600/20 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-lg w-full mx-auto space-y-5 relative z-10">
        {/* Brand Header */}
        <div className="flex items-center justify-center gap-2">
          <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-black text-xs shadow-lg shadow-indigo-500/30">
            PG
          </div>
          <span className="font-black text-lg tracking-tight text-white">PGastos</span>
        </div>

        {/* Main Card */}
        <div className="bg-slate-900/90 backdrop-blur-xl border border-slate-800 rounded-[2.2rem] p-6 sm:p-8 shadow-2xl space-y-6">
          {/* Creator & Concept */}
          <div className="text-center space-y-1.5">
            <span className="text-xs font-bold uppercase tracking-wider text-indigo-400">
              Cobro Compartido
            </span>
            <h1 className="text-xl sm:text-2xl font-black text-white">
              {data.creator_name}
            </h1>
            <p className="text-sm text-slate-400">
              {data.merchant ? `${data.merchant} • ` : ''}{data.concept}
            </p>
          </div>

          {/* Amount XXL */}
          <div className="text-center py-4 bg-slate-800/50 rounded-3xl border border-slate-800/80">
            <span className="text-[11px] font-bold text-slate-400 uppercase tracking-widest block mb-1">
              Monto a Pagar
            </span>
            <div className="text-4xl sm:text-5xl font-black text-emerald-400">
              ${data.amount.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              <span className="text-sm font-bold text-slate-400 ml-1">MXN</span>
            </div>

            {/* Status Badge */}
            <div className="mt-3 flex justify-center">
              <span className={`px-3 py-1 text-xs font-bold rounded-full flex items-center gap-1.5 ${
                isPaid
                  ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                  : hasReceipt
                    ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30'
                    : 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/30'
              }`}>
                {isPaid ? (
                  <>
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                    Pago Confirmado
                  </>
                ) : hasReceipt ? (
                  <>
                    <Clock className="w-3.5 h-3.5 text-amber-400" />
                    Comprobante enviado (por verificar)
                  </>
                ) : (
                  <>
                    <Clock className="w-3.5 h-3.5 text-indigo-400" />
                    Pendiente de Transferencia
                  </>
                )}
              </span>
            </div>

            {/* Plan de cuotas si aplica */}
            {data.payment_mode === 'installments' && data.installments > 1 && (
              <div className="mt-3.5 p-3 bg-indigo-950/60 border border-indigo-800/60 rounded-2xl text-left">
                <span className="text-[10px] uppercase font-bold text-indigo-400 tracking-wider block">
                  Plan en Cuotas Acordado
                </span>
                <p className="text-xs font-bold text-indigo-200 mt-0.5">
                  {data.installments} pagos mensuales de ${data.installment_amount?.toFixed(2) || (data.amount / data.installments).toFixed(2)} MXN
                </p>
              </div>
            )}
          </div>

          {/* Desglose de Artículos asignados si existen */}
          {data.shared_items && data.shared_items.length > 0 && (
            <div className="space-y-2">
              <span className="text-xs font-bold text-slate-400 uppercase tracking-wider block">
                Tus Artículos Asignados ({data.shared_items.length})
              </span>
              <div className="bg-slate-950/70 rounded-2xl border border-slate-800 divide-y divide-slate-800/60 overflow-hidden text-xs">
                {data.shared_items.map((item, idx) => (
                  <div key={item.id || idx} className="p-3 flex items-center justify-between">
                    <span className="font-semibold text-slate-300">{item.name || item.title}</span>
                    <span className="font-black text-white">${Number(item.price || item.amount || 0).toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Bank Transfer Details with Individual Copy Buttons */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                Datos para Transferir
              </span>
              <span className="text-[11px] text-slate-500">SPEI / Interbancario</span>
            </div>

            <div className="bg-slate-950/70 rounded-2xl border border-slate-800 divide-y divide-slate-800/60 overflow-hidden text-xs">
              {/* Titular */}
              <div className="p-3.5 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5 text-slate-400 font-medium text-[11px] mb-0.5">
                    <User className="w-3 h-3 text-indigo-400" />
                    Titular
                  </div>
                  <p className="font-bold text-white truncate">{data.bank_details?.holder_name || data.creator_name}</p>
                </div>
                <button
                  type="button"
                  onClick={() => copyToClipboard(data.bank_details?.holder_name || data.creator_name, 'Nombre del titular')}
                  className="p-2 bg-slate-800 hover:bg-slate-700 text-indigo-400 rounded-xl transition-colors shrink-0"
                  title="Copiar Titular"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>

              {/* Banco */}
              <div className="p-3.5 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5 text-slate-400 font-medium text-[11px] mb-0.5">
                    <Building className="w-3 h-3 text-indigo-400" />
                    Banco
                  </div>
                  <p className="font-bold text-white truncate">{data.bank_details?.bank_name || 'Banco'}</p>
                </div>
                <button
                  type="button"
                  onClick={() => copyToClipboard(data.bank_details?.bank_name || 'Banco', 'Banco')}
                  className="p-2 bg-slate-800 hover:bg-slate-700 text-indigo-400 rounded-xl transition-colors shrink-0"
                  title="Copiar Banco"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>

              {/* CLABE */}
              <div className="p-3.5 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5 text-slate-400 font-medium text-[11px] mb-0.5">
                    <Hash className="w-3 h-3 text-indigo-400" />
                    CLABE Interbancaria
                  </div>
                  <p className="font-mono font-bold text-emerald-400 text-sm tracking-wide truncate">
                    {data.bank_details?.clabe || 'Consultar con el titular'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => copyToClipboard(data.bank_details?.clabe, 'CLABE')}
                  className="p-2 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 rounded-xl transition-colors shrink-0"
                  title="Copiar CLABE"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Subir Comprobante */}
          {!isPaid && (
            <div className="pt-2">
              <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">
                {hasReceipt ? 'Comprobante Adjuntado' : 'Subir Comprobante de Pago'}
              </label>

              {hasReceipt ? (
                <div className="p-3.5 bg-emerald-500/10 border border-emerald-500/30 rounded-2xl flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />
                    <span className="text-xs font-bold text-emerald-300">
                      Comprobante recibido exitosamente
                    </span>
                  </div>
                  <label className="text-[11px] font-bold text-indigo-400 hover:underline cursor-pointer">
                    Cambiar
                    <input
                      type="file"
                      accept="image/*,application/pdf"
                      onChange={handleReceiptFile}
                      className="hidden"
                    />
                  </label>
                </div>
              ) : (
                <label className="w-full py-4 border-2 border-dashed border-slate-700 hover:border-indigo-500 rounded-2xl bg-slate-800/40 hover:bg-slate-800/80 cursor-pointer flex flex-col items-center justify-center gap-1.5 transition-all">
                  <Upload className="w-6 h-6 text-indigo-400" />
                  <span className="text-xs font-bold text-white">
                    {uploadingReceipt ? 'Subiendo comprobante...' : 'Toca para adjuntar foto de la transferencia'}
                  </span>
                  <span className="text-[10px] text-slate-500">JPG, PNG o PDF (máx 5MB)</span>
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    disabled={uploadingReceipt}
                    onChange={handleReceiptFile}
                    className="hidden"
                  />
                </label>
              )}
            </div>
          )}

          {/* Banner Viral de Crecimiento & Vinculación */}
          <div className="p-5 bg-gradient-to-br from-indigo-900/40 via-purple-900/30 to-slate-900 rounded-3xl border border-indigo-500/30 space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-xl bg-indigo-600/30 text-indigo-400 flex items-center justify-center shrink-0 mt-0.5">
                <Sparkles className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-xs font-bold text-white">
                  ¿Quieres llevar el control de tus deudas y gastos?
                </h4>
                <p className="text-[11px] text-slate-300 mt-0.5">
                  Regístrate gratis o inicia sesión para guardar esta deuda en tu cuenta y ver tus pagos en tiempo real.
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setShowClaimDialog(true)}
              className="w-full py-2.5 px-4 bg-white text-slate-950 hover:bg-slate-100 font-bold rounded-xl text-xs flex items-center justify-center gap-1.5 shadow-lg shadow-white/10 transition-all"
            >
              Vincular esta deuda a mi cuenta en PGastos
              <ExternalLink className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-slate-600">
          PGastos • Finanzas personales y control de pagos compartidos
        </p>
      </div>

      {/* Modal de Doble Check para Vinculación */}
      {showClaimDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="w-full max-w-sm bg-slate-900 border border-slate-800 rounded-3xl p-6 shadow-2xl space-y-4">
            <div className="flex items-center justify-between">
              <div className="w-10 h-10 rounded-2xl bg-indigo-600/20 text-indigo-400 flex items-center justify-center">
                <CreditCard className="w-5 h-5" />
              </div>
              <button
                type="button"
                onClick={() => setShowClaimDialog(false)}
                className="w-8 h-8 rounded-full bg-slate-800 text-slate-400 hover:text-white flex items-center justify-center"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div>
              <h3 className="text-base font-black text-white">
                ¿Ya tienes una cuenta en PGastos?
              </h3>
              <p className="text-xs text-slate-400 mt-1">
                Vincularemos este cobro automáticamente a tu panel de Control de Deudas para que puedas consultar tus pagos.
              </p>
            </div>

            <div className="space-y-2 pt-2">
              <button
                type="button"
                onClick={() => handleProceedToAuth('login')}
                className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-2xl text-xs transition-colors shadow-lg shadow-indigo-600/25"
              >
                Sí, Iniciar Sesión y Vincular Deuda
              </button>

              <button
                type="button"
                onClick={() => handleProceedToAuth('register')}
                className="w-full py-3 bg-slate-800 hover:bg-slate-700 text-slate-200 font-bold rounded-2xl text-xs transition-colors border border-slate-700"
              >
                No, Crear Cuenta Gratis en PGastos
              </button>

              <button
                type="button"
                onClick={() => setShowClaimDialog(false)}
                className="w-full py-2 text-slate-400 hover:text-white text-xs font-semibold"
              >
                Volver / Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicSharedExpense;
